import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar, Clock, Loader2, Video, MapPin, MessageSquare, Stethoscope,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useClinic } from '@/contexts/ClinicContext';
import { emrService, EmrAppointment } from '@/services/emrService';
import VisitActionButton from '@/components/visit/VisitActionButton';

/**
 * Patient-overview "Upcoming Visits" panel.
 *
 * The first thing a practitioner should see when opening a chart is when
 * they're next seeing this patient. This panel pulls all of the patient's
 * appointments and surfaces:
 *   - any visit currently in_progress    (pinned at top, "Resume Visit")
 *   - the next 2 future visits           ("Start Visit")
 *   - the most recent completed visit    ("Open Visit", subdued styling)
 *
 * Each row is a single click into the cockpit via VisitActionButton.
 */

interface PatientUpcomingVisitsPanelProps {
  patientId: string;
  /** Hide the wrapping Card so it can be embedded inline. */
  embedded?: boolean;
}

const isFuture = (a: EmrAppointment) =>
  a.start_time ? new Date(a.start_time).getTime() >= Date.now() - 60 * 60 * 1000 : false;

const formatWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today at ${time}`;
  if (isTomorrow) return `Tomorrow at ${time}`;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const visitModeIcon = (a: EmrAppointment) => {
  if (a.visit_mode === 'video' || a.is_telehealth) return Video;
  if (a.visit_mode === 'async_message') return MessageSquare;
  return MapPin;
};

const PatientUpcomingVisitsPanel: React.FC<PatientUpcomingVisitsPanelProps> = ({
  patientId, embedded,
}) => {
  const { clinic } = useClinic();
  const clinicId = clinic?.id || '';

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ['patient', patientId, 'appointments', 'upcoming'],
    queryFn: () => emrService.getPatientAppointments(clinicId, patientId),
    enabled: !!clinicId && !!patientId,
  });

  const { inProgress, upcoming, mostRecentCompleted } = useMemo(() => {
    const sorted = [...appointments].sort((a, b) => {
      const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
      const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
      return ta - tb;
    });
    const inProgress = sorted.filter(a => a.status === 'in_progress');
    const upcoming = sorted
      .filter(a => isFuture(a) && ['scheduled', 'confirmed', 'checked_in'].includes(a.status))
      .slice(0, 2);
    const completed = sorted
      .filter(a => a.status === 'completed')
      .reverse();
    return {
      inProgress,
      upcoming,
      mostRecentCompleted: completed[0] ?? null,
    };
  }, [appointments]);

  const hasAny = inProgress.length > 0 || upcoming.length > 0 || mostRecentCompleted;

  const Body = (
    <div className='space-y-2'>
      {isLoading ? (
        <div className='flex items-center gap-2 py-2 text-sm text-slate-500'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading appointments…
        </div>
      ) : !hasAny ? (
        <div className='rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500'>
          No upcoming or recent visits for this patient.
        </div>
      ) : (
        <>
          {inProgress.map(a => <Row key={a.id} appointment={a} highlight='in_progress' />)}
          {upcoming.map(a => <Row key={a.id} appointment={a} highlight='upcoming' />)}
          {mostRecentCompleted && (
            <Row appointment={mostRecentCompleted} highlight='completed' />
          )}
        </>
      )}
    </div>
  );

  if (embedded) return Body;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='flex items-center gap-2 text-base'>
          <Calendar className='h-4 w-4 text-brand-teal' />
          Upcoming visits
        </CardTitle>
      </CardHeader>
      <CardContent>{Body}</CardContent>
    </Card>
  );
};

const Row: React.FC<{
  appointment: EmrAppointment;
  highlight: 'in_progress' | 'upcoming' | 'completed';
}> = ({ appointment, highlight }) => {
  const ModeIcon = visitModeIcon(appointment);
  const isCompleted = highlight === 'completed';
  const isActive = highlight === 'in_progress';

  return (
    <div className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
      isActive
        ? 'border-amber-200 bg-amber-50'
        : isCompleted
          ? 'border-slate-200 bg-slate-50'
          : 'border-slate-200 bg-white'
    }`}>
      <div className='flex items-start gap-3 min-w-0'>
        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isActive
            ? 'bg-amber-100 text-amber-700'
            : isCompleted
              ? 'bg-slate-200 text-slate-600'
              : 'bg-brand-teal/10 text-brand-teal'
        }`}>
          {isCompleted ? <Stethoscope className='h-4 w-4' /> : <ModeIcon className='h-4 w-4' />}
        </div>
        <div className='min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-sm font-medium text-slate-900'>
              {appointment.serviceType?.name || appointment.title || 'Visit'}
            </span>
            {isActive && (
              <Badge className='bg-amber-100 text-amber-800 hover:bg-amber-100'>
                In progress
              </Badge>
            )}
            {isCompleted && (
              <Badge variant='outline' className='text-[10px]'>
                Last visit
              </Badge>
            )}
          </div>
          <div className='mt-0.5 flex items-center gap-1.5 text-xs text-slate-500'>
            <Clock className='h-3 w-3' />
            {formatWhen(appointment.start_time)}
            {appointment.practitioner && (
              <>
                <span>·</span>
                <span>
                  Dr. {appointment.practitioner.first_name} {appointment.practitioner.last_name}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <VisitActionButton
        appointment={appointment}
        variant={isActive ? 'primary' : 'outline'}
        size='sm'
      />
    </div>
  );
};

export default PatientUpcomingVisitsPanel;
